import { autoinject } from "aurelia-framework";

import { IdentityClient } from "../infrastructure/identity-client";

@autoinject
export class ProfileService {
  private readonly _client: IdentityClient;

  public constructor(client: IdentityClient) {
    this._client = client;
  }

  public async getProfile() {
    let response = await this._client
      .build("v1/profile")
      .fetch<IGetProfileResult>();

    return response;
  }

  public async getTotpStatus() {
    let response = await this._client
      .build("v1/profile/totp")
      .fetch<ITotpStatus>();

    return response;
  }

  public async enableTotp(command: IEnableTotpCommand) {
    let response = await this._client
      .build("v1/profile/totp")
      .useMethod("POST")
      .useBody(command)
      .fetch<IEnableTotpResponse>(false);

    return response;
  }

  public async getTotps() {
    let response = await this._client
      .build("v1/profile/organization/totps")
      .fetch<IEnableTotpsResponse>();

    return response;
  }

  public async disableTotp(code: string) {
    let command: IDisableTotpCommand = { code };
    let response = await this._client
      .build("v1/profile/totp")
      .useMethod("DELETE")
      .useQueryString(command)
      .fetch<IDisableTotpResponse>(false);

    return response;
  }
  public async generateTextOtp(payload: IPhoneTwoFAPayload) {
    let response = await this._client
      .build("v1/profile/otp")
      .useMethod("POST")
      .useBody(payload)
      .fetch<IPhoneTwoFAResponse>();
    return response;
  }

  public async generateCallOtp(payload: IPhoneTwoFAPayload) {
    let response = await this._client
      .build("v1/profile/otp/call")
      .useMethod("POST")
      .useBody(payload)
      .fetch<IPhoneTwoFAResponse>();
    return response;
  }

  public async enableMsgCallTwoFA(payload: IPhoneTwoFAPayload) {
    let response = await this._client
      .build("v1/profile/otp/complete")
      .useMethod("POST")
      .useBody(payload)
      .fetch<IPhoneTwoFAResponse>();
    return response;
  }

  public async generateTotpConfiguration() {
    let response = await this._client
      .build("v1/profile/totp/generate")
      .useMethod("POST")
      .fetch<IGenerateTotpResponse>();

    return response;
  }
  public async selectOtpCodePreference(
    ChallengeType: number | string = "text",
  ) {
    let response = await this._client
      .build("v1/profile/device/otp")
      .useMethod("POST")
      .useBody({
        ChallengeType,
      })
      .fetch<ISelectOtpCodePreference>();
    return response;
  }

  public async disableTextCallTwoFA(code: string, subjectId: string) {
    let response = await this._client
      .build("v1/profile/totp/otp")
      .useMethod("DELETE")
      .useQueryString({ Code: code, SubjectId: subjectId })
      .fetch<IDisableTotpResponse>();

    return response;
  }

  public async updatePassword(currentPassword: string, newPassword: string) {
    let response = await this._client
      .build("v1/profile/password")
      .useMethod("PUT")
      .useBody<IChangePasswordCommand>({ currentPassword, newPassword })
      .fetchOkErrors<{}>(false);

    return response;
  }

  public async updateName(firstName: string, lastName: string) {
    let response = await this._client
      .build("v1/profile/name")
      .useMethod("PUT")
      .useBody<IChangeNameCommand>({ firstName, lastName })
      .fetch<{}>(false);

    return response;
  }
  public async getAuditEvents(query: IGetAuditEventsQuery) {
    let response = await this._client
      .build("v1/profile/events")
      .useQueryString<IGetAuditEventsQuery>(query)
      .fetch<IGetAuditEventsResult>(false);

    return response;
  }

  public async getSecurityRegisterationPayload(requestQuery: any) {
    let response = await this._client
      .build("v1/profile/security-key/generate")
      .useMethod("POST")
      .useBody(requestQuery)
      .fetch<{}>();
    return response;
  }

  public async verifySecurityKey(payload: any) {
    let response = await this._client
      .build("v1/profile/security-key")
      .useMethod("POST")
      .useBody(payload)
      .fetch<{}>();
    return response;
  }

  public async enableSecurityKey2fa(payload: any) {
    let response = await this._client
      .build("v1/profile/security-key/turn-on")
      .useMethod("POST")
      .useBody(payload)
      .fetch();
    return response;
  }

  public async disableSecurityKey2fa(payload: any) {
    let response = await this._client
      .build("v1/profile/totp/security-key")
      .useMethod("DELETE")
      .useBody(payload)
      .fetch();
    return response;
  }

  public async getChallengeOptions() {
    let response = await this._client
      .build("v1/profile/security-key/challenge-options")
      .useMethod("POST")
      .fetch();
    return response;
  }
}

export interface ITotpStatus {
  active: boolean;
  enabledOn: string;
  challengeType: number;
  mobileNumber:string;
}

export interface ISelectOtpCodePreference {
  isSuccessful: string;
  message: string;
  statusCode?: number;
}

export interface IDisableTotpCommand {
  code: string;
}

export interface IDisableTotpResponse {
  subjectId: string;
  configurationId: string;
  isSuccessful?: boolean;
  message?: string;
  statusCode?:number;

}

export interface IEnableTotpCommand {
  secret: string;
  algorithm: string;
  digits: number;
  period: number;
  code: string;
}

export interface IEnableTotpResponse {
  subjectId: string;
  configurationId: string;
}

export interface IEnableTotpsResponse {
 cutoffDate: string;
 strongestChallengeType: number;
 totps: number[];

}
export interface IPhoneTwoFAResponse {
  isSuccessful: string;
  message: string;
  statusCode?: number;
  recoveryCode?: string;
}

export interface IPhoneTwoFAPayload {
  mobileNumber: string;
  code?: string;
  ResendOtp?: boolean;
}

export interface IGenerateTotpResponse {
  secret: string;
  algorithm: string;
  digits: number;
  period: number;
}

export interface IChangePasswordCommand {
  currentPassword: string;
  newPassword: string;
}

export interface IChangeNameCommand {
  firstName: string;
  lastName: string;
}

export interface IGetProfileResult {
  subjectId: string;
  firstName: string;
  lastName: string;
  email: string;
  passwordChangedOn: string;
}

export interface IGetAuditEventsQuery {
  include?: string;
  exclude?: string;
}

export interface IGetAuditEventsResult {
  events: IGetAuditEventsAuditEvent[];
}

export interface IGetAuditEventsAuditEvent {
  id: string;
  action: string;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}
